import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BUSINESS = {
  oneTime: 1000,
  maintenance: 79,
  seo: 199,
};

const FEATURES = [
  "Sito business completo (fino a 10 pagine)",
  "Design personalizzato & copywriting base",
  "Hosting high-speed, dominio e SSL inclusi",
  "Backup giornalieri & sicurezza gestita",
  "Aggiornamenti contenuti ogni mese",
  "Area clienti con report e assistenza",
];

const SEO_FEATURES = [
  "Audit SEO mensile & posizionamento keyword",
  "2 articoli SEO al mese",
  "Ottimizzazione Google Business Profile",
  "Report SEO trasparente in dashboard",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [seoOn, setSeoOn] = useState(true);
  const [loading, setLoading] = useState(false);

  const monthly = BUSINESS.maintenance + (seoOn ? BUSINESS.seo : 0);
  const firstYear = BUSINESS.oneTime + monthly * 12;
  const secondYear = monthly * 12;
  const fmt = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  const startCheckout = async () => {
    const payload = { plan_id: "business", addon_keys: seoOn ? ["seo_growth"] : [] };
    if (!user) {
      sessionStorage.setItem("pending_checkout", JSON.stringify(payload));
      navigate("/accedi");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        ...payload,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  const [paypalLoading, setPaypalLoading] = useState(false);

  const startPaypalSub = async () => {
    setPaypalLoading(true);
    try {
      const { data } = await api.post("/paypal/subscription", {
        seo_on: seoOn,
        email: user?.email || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaypalLoading(false);
    }
  };

  const startPaypal = async () => {
    setPaypalLoading(true);
    try {
      const { data } = await api.post("/paypal/order", {
        seo_on: seoOn,
        email: user?.email || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch {
      api.post("/paypal/intent", { seo_on: seoOn, email: user?.email || null }).catch(() => {});
      window.open("https://www.paypal.me/Marcello3425/1000EUR", "_blank", "noopener");
      setPaypalLoading(false);
    }
  };

  return (
    <section id="piani" data-testid="pricing-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <p className="mono-label text-[#E05638] mb-6">03 // Piani & Abbonamenti</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <h2 className="font-display font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
            Trasparenza totale. Scegli la tua soluzione.
          </h2>
          <p className="text-neutral-600 max-w-sm text-base leading-relaxed">
            Creazione una tantum + abbonamento mensile. Disdici quando vuoi.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid lg:grid-cols-5 gap-6 items-stretch"
        >
          <div data-testid="plan-card-business" className="lg:col-span-3 relative rounded-2xl bg-[#1E293B] text-white p-8 sm:p-10 flex flex-col shadow-[0_30px_60px_-20px_rgba(30,41,59,0.5)]">
            <span className="mono-label w-max px-3 py-1.5 rounded-full bg-[#E05638] text-white">Offerta completa</span>
            <h3 className="font-display font-bold text-2xl sm:text-3xl mt-6">Sito Business</h3>
            <p className="text-neutral-300 text-sm mt-2 leading-relaxed max-w-md">
              Creazione professionale del tuo sito + manutenzione continua ogni mese. Attiva la SEO
              Growth per scalare Google.
            </p>

            <div className="mt-8 flex flex-wrap items-end gap-x-10 gap-y-5">
              <div>
                <p data-testid="plan-onetime-price" className="font-display font-extrabold text-4xl sm:text-5xl">€1.000</p>
                <p className="text-xs text-neutral-400 mt-2">creazione, una tantum</p>
              </div>
              <div className="pb-1 pl-10 border-l border-white/15">
                <p className="font-display font-extrabold text-4xl sm:text-5xl text-[#E05638]">€79</p>
                <p className="text-xs text-neutral-400 mt-2">manutenzione, al mese</p>
              </div>
            </div>

            <div className="mt-8 rounded-xl bg-white/5 border border-white/10 p-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-display font-bold text-lg">
                  SEO Growth <span className="text-[#E05638]">€199/mese</span>
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  Audit mensile, 2 articoli SEO, Google Business Profile
                </p>
              </div>
              <button
                data-testid="seo-growth-toggle"
                onClick={() => setSeoOn(!seoOn)}
                className={`relative w-14 h-8 rounded-full transition-colors duration-300 shrink-0 ${seoOn ? "bg-[#E05638]" : "bg-white/15"}`}
                aria-label="Attiva o disattiva SEO Growth"
              >
                <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all duration-300 ${seoOn ? "left-7" : "left-1"}`} />
              </button>
            </div>

            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 flex-1">
              {[...FEATURES, ...(seoOn ? SEO_FEATURES : [])].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-200">
                  <Check size={16} className="text-[#E05638] mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              data-testid="plan-select-button-business"
              onClick={startCheckout}
              disabled={loading}
              className="mt-8 rounded-full py-3.5 text-sm font-semibold bg-[#E05638] text-white hover:bg-[#C8452A] transition-colors duration-300 disabled:opacity-60 inline-flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#E05638] focus:ring-offset-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Attiva Sito Business
            </button>

            <div className="mt-5 flex items-center gap-4">
              <span className="flex-1 h-px bg-white/10" />
              <span className="mono-label text-neutral-500">oppure</span>
              <span className="flex-1 h-px bg-white/10" />
            </div>

            <button
              data-testid="paypal-pay-button"
              onClick={startPaypal}
              disabled={paypalLoading}
              className="mt-5 w-full rounded-full py-3.5 text-sm font-semibold bg-[#0070ba] text-white hover:bg-[#005ea6] transition-colors duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {paypalLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <span className="font-extrabold italic tracking-tight">PayPal</span>
              )}
              Paga la creazione con PayPal
            </button>
            <p className="text-xs text-neutral-400 mt-3 leading-relaxed text-center">
              Con PayPal paghi la creazione (€1.000) sul momento; l'abbonamento mensile
              {seoOn ? " e la SEO Growth" : ""} li attiviamo insieme subito dopo.
            </p>
            <button
              data-testid="paypal-subscribe-button"
              onClick={startPaypalSub}
              disabled={paypalLoading}
              className="mt-3 w-full text-xs font-medium text-neutral-300 hover:text-white transition-colors underline underline-offset-4 disabled:opacity-60"
            >
              Oppure attiva solo il canone mensile con PayPal (€{monthly}/mese)
            </button>
          </div>

          <div className="lg:col-span-2 rounded-2xl bg-white border border-black/10 p-8 sm:p-10 flex flex-col justify-between hover:border-[#111111] transition-colors duration-300">
            <div>
              <p className="mono-label text-neutral-500">Il tuo investimento</p>
              <div className="mt-8">
                <p className="mono-label text-[#E05638]">Primo anno</p>
                <p data-testid="plan-first-year" className="font-display font-extrabold text-4xl sm:text-5xl mt-2 tracking-tight">
                  €{fmt(firstYear)}
                </p>
                <p className="text-xs text-neutral-500 mt-2">
                  creazione €1.000 + €{monthly}/mese × 12
                </p>
              </div>
              <div className="mt-8 pt-8 border-t border-black/10">
                <p className="mono-label text-neutral-500">Dal secondo anno</p>
                <p data-testid="plan-second-year" className="font-display font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">
                  €{fmt(secondYear)}
                  <span className="text-base font-medium text-neutral-500">/anno</span>
                </p>
                <p className="text-xs text-neutral-500 mt-2">solo abbonamenti, niente più costi di creazione</p>
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-8 leading-relaxed">
              {seoOn
                ? "SEO Growth inclusa nel totale. "
                : "Senza SEO Growth. "}
              Nessun vincolo oltre il mese in corso: disdici quando vuoi.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
